import type { components, paths } from '@pba/types-openapi-client';
import { parseISO } from 'date-fns';
import type { GivingInterval } from '../giving';
import { type UploadedFile, createUploadedFile } from '../uploaded-file';
import type { SubscriptionStatus } from './subscription-status';
import type { TransactionStatus } from './transaction-status';

export type PayerBase = {
  firstName: string;
  id?: string;
  lastName: string;
  profilePhoto?: UploadedFile;
};

export type TransactionBase = {
  amount: number;
  createdAt: Date;
  id: string;
  status?: TransactionStatus;
  subscription?: SubscriptionBase;
};

export type TransactionBaseWithUser = {
  user?: PayerBase;
} & TransactionBase;

export type TransactionDetail = {
  transactionItems: TransactionBase[];
} & TransactionBase;

export type TransactionItemBase = {
  amount: number;
  anonymous: boolean;
  chargeType: GivingInterval;
  createdAt: Date;
  id: string;
  subscriptionId?: string;
};

export type TransactionItemDetail = {
  showTextContent: boolean;
  textContent?: string;
  transaction?: TransactionBaseWithUser;
} & TransactionItemBase;

type SubscriptionBase = {
  createdAt: Date;
  id: string;
  status: SubscriptionStatus;
};

export const createTransactionItemBase = (data: components['schemas']['TransactionItemBase']): TransactionItemBase => ({
  amount: data.amount,
  anonymous: data.anonymous,
  chargeType: data.charge_type,
  createdAt: parseISO(data.created_at),
  id: data.id,
  subscriptionId: data.subscription_id,
});

export const createTransactionItemDetail = (
  data: components['schemas']['TransactionItemDetail'],
): TransactionItemDetail => ({
  ...createTransactionItemBase(data),
  showTextContent: data.show_text_content,
  textContent: data.text_content,
  transaction: data.transaction ? createTransactionBaseWithUser(data.transaction) : undefined,
});

const createSubscriptionBase = (data: components['schemas']['SubscriptionBase']): SubscriptionBase => ({
  createdAt: parseISO(data.created_at),
  id: data.id,
  status: data.status,
});

export const createTransactionBase = (data: components['schemas']['TransactionBase']): TransactionBase => ({
  amount: data.amount,
  createdAt: parseISO(data.created_at),
  id: data.id,
  status: data.status,
  subscription: data.subscription ? createSubscriptionBase(data.subscription) : undefined,
});

export const createPayerBase = (data: components['schemas']['PayerBase']): PayerBase => ({
  firstName: data.first_name,
  id: data.id,
  lastName: data.last_name,
  profilePhoto: data.profile_photo ? createUploadedFile(data.profile_photo) : undefined,
});

export const createTransactionBaseWithUser = (
  data: components['schemas']['TransactionBaseWithUser'],
): TransactionBaseWithUser => ({
  ...createTransactionBase(data),
  user: data.payer ? createPayerBase(data.payer) : undefined,
});

export const createTransactionDetail = (data: components['schemas']['TransactionDetail']): TransactionDetail => ({
  ...createTransactionBase(data),
  transactionItems: data.items.map((item) => createTransactionItemBase(item)),
});

export type CheckoutTransaction = {
  checkoutUrl: string;
};

export const getCheckoutTransaction = (
  // TODO: Missing OpenAPI schema
  data: paths['/v3/transactions/{id}/checkout']['post']['responses']['200']['content']['application/json'],
): CheckoutTransaction => ({
  checkoutUrl: data.checkout_url,
});
